import type { Mode, Scene, Story } from "../types/story";
import { indexScenes } from "./loadStory";

/**
 * Scene navigation for both modes.
 *
 *  - PASSIVE: follow `canonicalPath` exactly, ignoring all choices. If a story
 *    has no canonicalPath, fall back to walking linearly from startSceneId
 *    following the FIRST option / implicit next.
 *  - INTERACTIVE: start at startSceneId; advancing past a scene with a choice
 *    requires a chosen option (handled by the UI). Scenes without a choice
 *    advance to the next scene by document order (a simple, predictable default
 *    for authored linear stretches).
 */

export interface Navigator {
  mode: Mode;
  /** The first scene to show. */
  start(): Scene | undefined;
  /**
   * Given the current scene and (for interactive choices) the picked option
   * index, return the next scene — or undefined if the story is over.
   */
  next(currentSceneId: string, chosenOptionIndex?: number): Scene | undefined;
  /** Does this scene present an interactive choice right now? */
  hasChoiceAt(sceneId: string): boolean;
}

export function makeNavigator(story: Story, mode: Mode): Navigator {
  const byId = indexScenes(story);
  const order = story.scenes.map((s) => s.id);

  // Passive path: prefer canonicalPath; else derive a linear walk.
  const passivePath =
    Array.isArray(story.canonicalPath) && story.canonicalPath.length > 0
      ? story.canonicalPath
      : derivePassivePath(story, byId, order);

  const passiveIndex = new Map<string, number>();
  passivePath.forEach((id, i) => passiveIndex.set(id, i));

  /**
   * Linear successor for a scene with no choice.
   *
   * The JSON contract only expresses linear continuation via `canonicalPath`
   * (a choiceless scene has no `nextSceneId`). Document order alone breaks at
   * branch *reconvergence* points — e.g. canonicalPath [s1,s2,s3a,s4] expects
   * s3a -> s4, but s3b sits between them in document order. So:
   *   1. If the scene is on canonicalPath, follow canonicalPath.
   *   2. Otherwise (off the canonical path), fall back to document order.
   */
  function nextLinear(sceneId: string): Scene | undefined {
    const ci = passiveIndex.get(sceneId);
    if (ci !== undefined) {
      const nextId = passivePath[ci + 1];
      return nextId ? byId.get(nextId) : undefined;
    }
    const i = order.indexOf(sceneId);
    if (i < 0 || i + 1 >= order.length) return undefined;
    return byId.get(order[i + 1]);
  }

  return {
    mode,

    start() {
      if (mode === "passive") {
        return byId.get(passivePath[0] ?? story.startSceneId);
      }
      return byId.get(story.startSceneId);
    },

    hasChoiceAt(sceneId) {
      if (mode === "passive") return false; // choices are hidden in passive.
      const scene = byId.get(sceneId);
      return !!(scene?.choice && scene.choice.options?.length);
    },

    next(currentSceneId, chosenOptionIndex) {
      // Passive ignores all choices and just walks the canonical/linear path.
      if (mode === "passive") {
        return nextLinear(currentSceneId);
      }

      // Interactive
      const scene = byId.get(currentSceneId);
      const options = scene?.choice?.options;
      if (options && options.length) {
        if (chosenOptionIndex === undefined) return undefined; // awaiting choice
        const picked = options[chosenOptionIndex];
        return picked ? byId.get(picked.nextSceneId) : undefined;
      }
      return nextLinear(currentSceneId);
    },
  };
}

/**
 * Fallback passive path when canonicalPath is absent: walk from startSceneId,
 * always taking the first choice option, until we run out or revisit a scene.
 */
function derivePassivePath(
  story: Story,
  byId: Map<string, Scene>,
  order: string[]
): string[] {
  const path: string[] = [];
  const seen = new Set<string>();
  let current: string | undefined = story.startSceneId ?? order[0];

  while (current && byId.has(current) && !seen.has(current)) {
    path.push(current);
    seen.add(current);
    const scene: Scene = byId.get(current)!;
    const first: string | undefined = scene.choice?.options?.[0]?.nextSceneId;
    if (first) {
      current = first;
    } else {
      const i = order.indexOf(current);
      current = i >= 0 ? order[i + 1] : undefined;
    }
  }
  return path;
}
